import React, { useState } from "react";
import TextBlock from "../TextBlock/TextBlock";
import ContractSign from "./ContractSign";
import PdfDownload from "../PdfDownload";
import PositionList from "../Positions/PositionList";
import SubjectDate from "../SubjectDate";
import ContractHead from "./ContractHead";
import FooterContact from "../FooterContact";
import { ResourceContextProvider } from "../../Service/Context/ResourceContext";
import {
  Button,
  Center,
  Group,
  Loader,
  Space,
  Text,
  Textarea,
} from "@mantine/core";
import VatNote from "../Document/VatNote";
import { Icon } from "../../Atoms/Icons";
import Moment from "react-moment";
import { withdrawContract } from "../../Service/restapi/sharedContractService";
import { useToasts } from "react-toast-notifications";
import { closeAllModals, openModal } from "@mantine/modals";

const Contract = ({
  resource,
  document,
  formSubmission,
  values,
  handleChange,
  reloadFormSubmission,
  reloadContract,
  patchSubmissionValue,
}) => {
  const isCancelled =
    document.cancelledAt !== null && document.cancelledAt !== undefined;
  const isSigned =
    formSubmission.lastSubmitted !== null &&
    formSubmission.lastSubmitted !== undefined;

  return (
    <ResourceContextProvider
      resourceId={resource.id}
      contactId={document.contactId}
      customerContactId={document.customerId}
    >
      {isCancelled && (
        <CancelledContractNotice cancelledAt={document.cancelledAt} />
      )}
      <ContractHead contract={document} resource={resource} />
      <SubjectDate subject={document.subject} date={document.date} />
      {document.aboveTextBlocks.map((textBlock, index) => {
        return (
          <TextBlock key={"aboveTextBlock" + index} textBlock={textBlock} />
        );
      })}
      <PositionList
        resourceId={resource.id}
        taxType={document.taxType}
        positions={document.positions}
        isSummable={document.summable}
        formSubmission={formSubmission}
        values={values}
        handleChange={handleChange}
        document={document}
      />
      {document.positions && document.positions.length !== 0 && (
        <>
          <Space h={30} />
          <VatNote />
        </>
      )}
      {document.belowTextBlocks.map((textBlock, index) => {
        return (
          <TextBlock key={"belowTextBlock" + index} textBlock={textBlock} />
        );
      })}
      <FooterContact contract={document} />
      {document.contractSign !== "NONE" && !isCancelled && (
        <ContractSign
          contract={document}
          formSubmission={formSubmission}
          customerSignType={document.customerSignType}
          patchSubmissionValue={patchSubmissionValue}
          values={values}
          handleChange={handleChange}
          resource={resource}
          reload={reloadFormSubmission}
        />
      )}
      <Space h="xl" />
      <PdfDownload
        formSubmission={formSubmission}
        text="Vertrag herunterladen"
      />
      {/* {!isCancelled && isSigned && (
        <WithdrawContractAction
          contractId={document.id || resource.entityId}
          reloadContract={reloadContract}
          reloadFormSubmission={reloadFormSubmission}
        />
      )} */}
    </ResourceContextProvider>
  );
};

const CancelledContractNotice = ({ cancelledAt }) => {
  const styles = {
    container: {
      border: "2px solid #d9480f",
      borderLeftWidth: 8,
      color: "#7c2d12",
      background:
        "linear-gradient(135deg, rgba(255, 244, 230, 0.95), rgba(255, 236, 217, 0.95))",
      padding: "18px 22px",
      marginBottom: 30,
      overflow: "hidden",
    },
    topLine: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 10,
      marginBottom: 6,
    },
    stamp: {
      border: "2px solid rgba(217, 72, 15, 0.55)",
      color: "rgba(217, 72, 15, 0.75)",
      fontWeight: 800,
      letterSpacing: 2,
      padding: "4px 10px",
      transform: "rotate(-8deg)",
      textTransform: "uppercase",
    },
    headline: {
      fontSize: 20,
      fontWeight: 700,
    },
    text: {
      margin: 0,
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.topLine}>
        <div style={styles.headline}>
          <Icon value="CANCELED" /> Dieser Vertrag wurde zurückgezogen
        </div>
        <div style={styles.stamp}>Zurückgezogen</div>
      </div>
      <p style={styles.text}>
        Seit dem <Moment format="L LT">{cancelledAt}</Moment> ist dieser Vertrag
        nicht mehr gültig. Eine Unterzeichnung ist deshalb nicht mehr möglich.
      </p>
    </div>
  );
};

const WithdrawContractAction = ({
  contractId,
  reloadContract,
  reloadFormSubmission,
}) => {
  const { addToast } = useToasts();

  const onClick = () =>
    openModal({
      title: (
        <Text weight="bolder" size="xl">
          Vertrag zurückziehen?
        </Text>
      ),
      children: (
        <WithdrawContractModal
          contractId={contractId}
          reloadContract={reloadContract}
          reloadFormSubmission={reloadFormSubmission}
          addToast={addToast}
        />
      ),
    });

  return (
    <>
      <Space h="xl" />
      <hr />
      <Center>
        <Button
          color="red"
          variant="outline"
          style={{ height: 42, marginTop: 12 }}
          onClick={onClick}
        >
          Vertrag zurückziehen
        </Button>
      </Center>
    </>
  );
};

const WithdrawContractModal = ({
  contractId,
  reloadContract,
  reloadFormSubmission,
  addToast,
}) => {
  const [reasonOfWithDrawning, setReasonOfWithDrawning] = useState("");
  const [loading, setLoading] = useState(false);

  const withdraw = (event) => {
    event.preventDefault();
    setLoading(true);
    withdrawContract(contractId, reasonOfWithDrawning.trim())
      .then((resp) => {
        if (resp.ok) {
          closeAllModals();
          if (reloadContract) {
            reloadContract();
          }
          if (reloadFormSubmission) {
            reloadFormSubmission();
          }
          addToast("Der Vertrag wurde zurückgezogen.", {
            appearance: "success",
          });
        } else {
          addToast(
            "Der Vertrag konnte nicht zurückgezogen werden. Bitte versuche es später erneut.",
            { appearance: "error" }
          );
        }
      })
      .catch(() => {
        addToast(
          "Der Vertrag konnte nicht zurückgezogen werden. Bitte prüfe Deine Verbindung.",
          { appearance: "error" }
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <form onSubmit={withdraw}>
      <Text mb="md">
        Möchtest Du diesen Vertrag wirklich zurückziehen? Danach kann er nicht
        mehr unterzeichnet werden.
      </Text>
      <Textarea
        minRows={4}
        name="reason_of_withdrawning"
        value={reasonOfWithDrawning}
        onChange={(e) => setReasonOfWithDrawning(e.target.value)}
        placeholder="Grund für das Zurückziehen (optional)"
      />
      <Group position="right" mt={30}>
        <Button
          variant="default"
          disabled={loading}
          onClick={() => closeAllModals()}
        >
          Abbrechen
        </Button>
        <Button
          color="red"
          type="submit"
          disabled={loading}
          rightIcon={loading && <Loader size="xs" />}
        >
          Vertrag zurückziehen
        </Button>
      </Group>
    </form>
  );
};

export default Contract;
